/**
 * inboxFilters — PURE filter-state + filter-application for the Unified Inbox
 * list (M-UX-5.2, `architecture-ux-restructure.md` §6.5 "Filter-Chips").
 *
 * Two independent, AND-combined filters: Kanal (Upload/E-Mail/Scan) and Status.
 * `'all'` is the neutral, always-present option for both dimensions. Kept
 * side-effect-free and hook-free so it is trivially unit-testable and reusable
 * from both the page and its tests without mounting React.
 *
 * @module components/inbox/inboxFilters
 */

import type { UnifiedChannel, UnifiedInboxItem, UnifiedStatus } from '../../utils/unifiedInbox/types';

/** Kanal-Filterwert — `'all'` zusätzlich zu den drei echten Kanälen. */
export type ChannelFilterValue = UnifiedChannel | 'all';

/** Status-Filterwert — `'all'` zusätzlich zu den sechs echten Status. */
export type StatusFilterValue = UnifiedStatus | 'all';

/** Aktueller Filter-Zustand der Liste (§6.5 "Filter-Chips: Kanal + Status"). */
export interface UnifiedInboxFilterState {
  channel: ChannelFilterValue;
  status: StatusFilterValue;
}

/** Neutraler Ausgangszustand — zeigt alle Einträge. */
export const DEFAULT_FILTER_STATE: UnifiedInboxFilterState = { channel: 'all', status: 'all' };

/**
 * Applies both filter dimensions (AND-combined) to a merged+sorted item list.
 * Pure — never mutates `items`, never throws (an invalid/unknown filter value
 * simply matches nothing for that dimension rather than crashing the list).
 *
 * @param items the full (already merged+sorted) Unified Inbox item list.
 * @param filter the current filter state.
 * @returns the filtered subset, preserving the input order.
 */
export function filterUnifiedInboxItems(
  items: readonly UnifiedInboxItem[],
  filter: UnifiedInboxFilterState,
): UnifiedInboxItem[] {
  return items.filter((item) => {
    if (filter.channel !== 'all' && item.channel !== filter.channel) return false;
    if (filter.status !== 'all' && item.status !== filter.status) return false;
    return true;
  });
}

/**
 * Maps the `?stream=` deep-link query param (§8: Redirect von den Alt-Routen,
 * `/posteingang` → `?stream=email` etc.) onto a valid {@link ChannelFilterValue}.
 * Unknown/missing values fall back to `'all'` (fail-closed — never crash the
 * list on a malformed/foreign query string).
 *
 * @param streamParam raw `URLSearchParams.get('stream')` result.
 * @returns the matching channel filter, or `'all'` when not a valid channel.
 */
export function channelFilterFromStreamParam(streamParam: string | null): ChannelFilterValue {
  if (streamParam === 'upload' || streamParam === 'email' || streamParam === 'scan') {
    return streamParam;
  }
  return 'all';
}
